import { lastPanel, panelUrl } from '../Shell/panelFollow';
import { PANELS, HOME_PATH, ABOUT_YOU_PATH } from '../Shell/panelRoutes';
import { useShell } from '../Shell/ShellContext';
import { useGettingStarted } from '../../context/GettingStartedContext';
import { FACTS_CHANGED, OPEN_SETUP, openSetup } from '../../services/aboutYouEvents';
import ConversationHeader from './ConversationHeader';
import { autoApplyCard, autoApplySummary, autoApplyLines, stripToolEcho } from './autoApplyCard';
import React, { useState, useRef, useEffect, useMemo, useCallback, Suspense, lazy } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useOmniAgent } from '../../context/OmniAgentContext';
import { turnFooter } from '../../services/usageFormat';
import { useProfileData } from '../../context/ProfileDataContext';
import { useAuth } from '../AuthContext';
import DiffCard from './DiffCard';
import ArrangeCard from './ArrangeCard';
import AskOnce from './AskOnce';
import { fetchSetup, saveSetup, confirmSetup } from '../../services/setup';
import { usePanelResults } from '../../context/PanelResultsContext';
import { stepView, visibleSteps, planOf, statusLabel, turnClock } from './workSteps';
import { isAdminUser } from '../../utils/admin';
// The draft review card's verbs — same routes the queue row uses, so the two
// surfaces can never disagree about what approval means.
import { approveDraft, discardDraft } from '../../services/answerBank';
import MessageActions from './MessageActions';
import ModelPicker from './ModelPicker';
import UsageMeter from './UsageMeter';
// Markdown parsed once per distinct text, tables included (replyMarkdown.js).
import { Md, preloadMarkdown } from './replyMarkdown';
import { stripNext } from './replyText';

import './AntigravitySidebar.css';
import { useFirstQuestion } from '../../utils/pendingPrompt';


// The one status line while a turn works (chat-2, 2026-10-30), in place of
// the list of steps: a spinner, what is happening in the server's words
// ("Reading your resume…", "Searching 889,000 jobs…"), one clock for the
// whole turn (it never restarts between steps), and "Still working" once
// fifteen seconds pass with nothing new (founder, 2026-09-27: a minute of
// "thinking" read as stuck). Each status event replaces the line; the reply's
// first words clear it. The steps themselves fold under "Worked for 38s"
// once the turn is done.
//
// Its own component, outside the sidebar, so its ticking clock survives the
// sidebar re-rendering on every streamed event.
function StatusLine({ msg, label }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);
  const started = msg.turnStartedAt || msg.live?.since || now;
  const quiet = now - (msg.lastEventAt || started);
  return (
    <div className="ag-steps is-live">
      <div className="ag-tool-block ag-tool-running ag-tool-live ag-status-line">
        <div className="ag-spinner" aria-hidden="true" />
        {/* Only the words are announced: the clock would be read out twice a second. */}
        <span className="ag-status-label" role="status" aria-live="polite">{label}</span>
        <span className="ag-tool-elapsed" aria-hidden="true">{turnClock(now - started)}</span>
        {quiet > 15_000 && <span className="ag-tool-slow">Still working</span>}
      </div>
    </div>
  );
}

// The home screen's prompts (the approved design, 2026-09-27): plain words
// that send, one per thing the product does. They replace the dashboard's
// three cards, which only changed the page — the chat does the work now.
const HOME_PROMPTS = [
  { id: 'find', title: 'Find jobs for me', prompt: 'Find jobs that fit my resume.', icon: 'travel_explore' },
  { id: 'apply', title: 'Apply to my saved jobs', prompt: 'Apply to my saved jobs.', icon: 'send' },
  { id: 'tailor', title: 'Tailor my resume to a job', prompt: 'Tailor my resume to a job description.', icon: 'edit_document' },
  { id: 'prep', title: 'Prep me for an interview', prompt: 'Help me prepare for an interview.', icon: 'forum' },
];

// A phone's keyboard has no Shift, so Return is its only way to start a new
// line: on a touch screen Return breaks the line and only Send sends
// (2026-09-30). A computer keeps Enter to send, Shift+Enter for a line. A
// narrow window on a computer is still a computer: it has no touch points.
const returnBreaksLine = () => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(hover: none) and (pointer: coarse)')?.matches) return true;
  } catch { /* no matchMedia: decided by the width below */ }
  return window.innerWidth <= 768 && (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0);
};

// Two sets, because the same four prompts cannot serve both states.
//
// These were one list, shown whenever the chat was empty in Design Studio,
// with no check for whether a resume existed. Every one of them assumes
// content that is already there: a brand-new user was offered "make my
// bullets more concise" with no bullets, "fit onto one page" with no resume,
// and "switch template" with a blank page to look at. Clicking any of them
// made the assistant's first act be explaining it had nothing to work with —
// which reads as the product being broken, on the first screen someone opens.
// Design Studio's opening screen, in the same shape as the dashboard's: an
// icon, a short title and a line saying what it will actually do. The bare
// text chips this replaces gave a new user four unexplained commands and no
// sense of what the assistant was for.
//
// `prompt` is what gets sent; `title` is only what the card shows. They differ
// on purpose — a card reads better as a short label, while the assistant does
// better with a full sentence.
const RESUME_SUGGESTIONS = [
  {
    id: 'tighten',
    title: 'Tighten my bullets',
    desc: 'Cut them back so each one leads with what you achieved.',
    prompt: 'Make my experience bullets more concise',
    icon: 'compress', color: '#0ea5e9', bg: '#e0f2fe',
  },
  {
    id: 'one-page',
    title: 'Fit it on one page',
    desc: 'Trim the wording and tighten the spacing until it fits.',
    prompt: 'Fit my resume onto one page',
    icon: 'article', color: '#f59e0b', bg: '#fef3c7',
  },
  {
    id: 'template',
    title: 'Try another template',
    desc: 'Keep everything you have written and change how it looks.',
    prompt: 'Switch to a different template',
    icon: 'palette', color: '#8b5cf6', bg: '#ede9fe',
  },
  {
    id: 'tailor',
    title: 'Tailor it to a job',
    desc: 'Paste in a job posting and I will match your wording to it.',
    prompt: 'Tailor my resume for a job description',
    icon: 'my_location', color: '#10b981', bg: '#d1fae5',
  },
];

// Things the assistant can genuinely do with an empty resume: each of these
// ends in add_resume_entry proposing something, or in a straight answer.
// Offering "make my bullets more concise" to someone with no bullets made the
// assistant's first act be explaining it had nothing to work with.
const EMPTY_RESUME_SUGGESTIONS = [
  {
    id: 'job',
    title: 'Add my most recent job',
    desc: 'Tell me where you worked and what you did, and I will write it up.',
    prompt: 'Add my most recent job',
    icon: 'work', color: '#0ea5e9', bg: '#e0f2fe',
  },
  {
    id: 'education',
    title: 'Add my education',
    desc: 'Where you studied, what you studied and when.',
    prompt: 'Add my education',
    icon: 'school', color: '#f59e0b', bg: '#fef3c7',
  },
  {
    id: 'summary',
    title: 'Write a summary for me',
    desc: 'A short opening that says who you are and what you do.',
    prompt: 'Write a summary for me',
    icon: 'edit_note', color: '#8b5cf6', bg: '#ede9fe',
  },
  {
    id: 'sections',
    title: 'What sections should I include?',
    desc: 'I will suggest a layout that suits your background.',
    prompt: 'What sections should I include?',
    icon: 'help', color: '#10b981', bg: '#d1fae5',
  },
];

// The job agent's opening cards. Every one of these maps onto something the
// agent genuinely does — reading the answer bank and saving answers — so no
// card's first act can be explaining that it cannot help. Same lesson as the
// resume sets above.
const JOB_AGENT_SUGGESTIONS = [
  {
    id: 'interview',
    title: 'Ask me the open questions',
    desc: 'I ask, you answer in plain words, and we stop when the list is done.',
    prompt: 'Ask me the application questions I have not answered yet, one at a time.',
    icon: 'quiz', color: '#0ea5e9', bg: '#e0f2fe',
  },
  {
    id: 'salary',
    title: 'Set my salary expectation',
    desc: 'You say it once and I type it into every form that asks.',
    prompt: 'I want to set my desired salary for job applications.',
    icon: 'payments', color: '#f59e0b', bg: '#fef3c7',
  },
  {
    id: 'review',
    title: 'Read my answers back',
    desc: 'Everything I would say on a form, so you can check it.',
    prompt: 'Read back all the application answers you have for me so I can check them.',
    icon: 'fact_check', color: '#8b5cf6', bg: '#ede9fe',
  },
  {
    id: 'gaps',
    title: 'What is still missing?',
    desc: 'I list the questions nothing on file can answer yet.',
    prompt: 'Which application questions are still unanswered for me?',
    icon: 'checklist', color: '#10b981', bg: '#d1fae5',
  },
];

const INTERVIEW_COACH_SUGGESTIONS = [
  {
    id: 'mock-question',
    title: 'Ask me an interview question',
    desc: 'Practice answering behavioral and situational questions.',
    prompt: 'Ask me a behavioral interview question and coach my answer.',
    icon: 'psychology', color: '#8b5cf6', bg: '#ede9fe',
  },
  {
    id: 'star-method',
    title: 'Turn a story into STAR',
    desc: 'Structure a work accomplishment into Situation, Task, Action, and Result.',
    prompt: 'Help me turn a work accomplishment into a strong STAR interview story.',
    icon: 'auto_awesome', color: '#0ea5e9', bg: '#e0f2fe',
  },
  {
    id: 'tough-question',
    title: 'Practice tough questions',
    desc: 'Failures, conflicts, and challenges phrased to highlight your strengths.',
    prompt: 'How should I answer "Tell me about a time you failed" effectively?',
    icon: 'crisis_alert', color: '#f59e0b', bg: '#fef3c7',
  },
  {
    id: 'cheat-sheet',
    title: 'Prep for my interview call',
    desc: 'Key talking points, technical pillars, and questions to ask them.',
    prompt: 'Help me prepare my key talking points and cheat sheet for an interview call.',
    icon: 'fact_check', color: '#10b981', bg: '#d1fae5',
  },
];

const JOB_FEED_SUGGESTIONS = [
  {
    id: 'match-roles',
    title: 'Find best match jobs',
    desc: 'Filter the feed for roles aligned with your background and skills.',
    prompt: 'Show me the best matched job openings for my background.',
    icon: 'auto_awesome', color: '#10b981', bg: '#d1fae5',
  },
  {
    id: 'tailor-role',
    title: 'Tailor for a role',
    desc: 'Match your experience to requirements for a specific job title.',
    prompt: 'How can I best position myself for roles in this feed?',
    icon: 'my_location', color: '#0ea5e9', bg: '#e0f2fe',
  },
  {
    id: 'remote-roles',
    title: 'Search remote opportunities',
    desc: 'Find remote-first positions matching your career goals.',
    prompt: 'Help me find remote positions in my field.',
    icon: 'home_work', color: '#f59e0b', bg: '#fef3c7',
  },
  {
    id: 'salary-range',
    title: 'Compare salary expectations',
    desc: 'Benchmark compensation ranges for the jobs in your feed.',
    prompt: 'What salary ranges should I expect for these roles?',
    icon: 'payments', color: '#8b5cf6', bg: '#ede9fe',
  },
];

// ── What one reply, and the composer, cost to draw (2026-10-31) ─────────────
//
// The founder: "extremely slow user feel as well in touch and type". The
// typed words lived in this component's state, so every keystroke drew the
// whole conversation again: every reply's steps, cards, copy-and-rate row
// (each formatting its time with a new Intl formatter) and receipt. And every
// streamed word did the same for every finished reply above the one being
// written. Now the composer keeps its own words (Composer, below), and each
// message is its own memoised row: a finished reply is drawn again only when
// something about it changes. renderBudget.test.js holds the counts.

// Elapsed time for a tool step.
//
// A running step showed a spinner and nothing else, so a call that takes
// fifteen seconds looked exactly like one that had hung — the only honest
// signal available is how long it has actually been going. Ticks while
// running, then freezes at the final duration.
//
// Out here, not inside the chat: defined inside it, it was a new component
// on every render, so each step's clock was unmounted and mounted again on
// every keystroke and every streamed word.
function ToolElapsed({ tool }) {
  const [now, setNow] = useState(Date.now());
  const running = tool.status === 'running';

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [running]);

  const ms = running
    ? (tool.startedAt ? now - tool.startedAt : null)
    : (tool.elapsedMs ?? null);
  if (ms == null || ms < 400) return null; // sub-half-second is noise

  return (
    <span className="ag-tool-elapsed">
      {ms < 10000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms / 1000)}s`}
    </span>
  );
}

const parseMessageContent = (rawContent) => {
  if (!rawContent) return { thinking: null, body: '' };
  const str = String(rawContent);
  const match = str.match(/<(?:thought|thinking)>([\s\S]*?)<\/(?:thought|thinking)>/i);
  if (match) {
    const thinking = match[1].trim();
    // A thinking block is the model's, and so is an echoed tool transcript.
    const body = stripToolEcho(str.replace(/<(?:thought|thinking)>[\s\S]*?<\/(?:thought|thinking)>/i, '').trim());
    return { thinking, body };
  }
  return { thinking: null, body: str };
};

const AVAILABLE_SECTIONS = [
  { id: 'summaries', title: 'Summary' },
  { id: 'experience', title: 'Experience' },
  { id: 'education', title: 'Education' },
  { id: 'projects', title: 'Projects' },
  { id: 'skills', title: 'Skills' },
  { id: 'certifications', title: 'Certifications' }
];

const formatSectionType = (rawType) => {
  if (!rawType) return '';
  const t = String(rawType).toUpperCase();
  switch (t) {
    case 'EXPERIENCE': return 'Experience';
    case 'EDUCATION': return 'Education';
    case 'PROJECTS': return 'Projects';
    case 'SKILLS': return 'Skills';
    case 'SUMMARIES': return 'Summary';
    case 'CERTIFICATIONS': return 'Certifications';
    default: return t.charAt(0) + t.slice(1).toLowerCase();
  }
};

const getEntityName = (data) => {
  if (!data || typeof data !== 'object') return '';
  return data.companyName || data.schoolName || data.name || data.category || (data.position ? `(${data.position})` : '');
};

const getDynamicSectionLabel = (tool) => {
  const input = tool.input || {};
  let output = null;
  try {
    if (typeof tool.output === 'string') output = JSON.parse(tool.output);
    else if (tool.output && typeof tool.output === 'object') output = tool.output;
  } catch { /* ignore non-json */ }

  if (tool.name === 'get_resume' && output && Array.isArray(output.sections)) {
    return `Reading Resume (${output.sections.length} Sections & Design)`;
  }

  if (tool.name === 'update_resume_entry') {
    const type = output?.type || input?.type;
    const target = getEntityName(output?.newData || input?.newData || output?.oldData);
    const section = formatSectionType(type);
    if (section && target) return `Updating ${section} (${target})`;
    if (section) return `Updating ${section}`;
  }

  if (tool.name === 'add_resume_entry') {
    const type = input?.type || output?.type;
    const target = getEntityName(input?.data || output?.newData);
    const section = formatSectionType(type);
    if (section && target) return `Adding ${section} (${target})`;
    if (section) return `Adding to ${section}`;
  }

  if (tool.name === 'delete_resume_entry') {
    const type = output?.type || input?.type;
    const target = getEntityName(output?.oldData);
    const section = formatSectionType(type);
    if (section && target) return `Removing ${section} (${target})`;
    if (section) return `Removing from ${section}`;
  }

  if (tool.name === 'batch_update_resume') {
    const count = input?.updates?.length || output?.updates?.length;
    if (count && count > 1) return `Tailoring ${count} Resume Sections`;
  }

  if (tool.name === 'update_design_settings') {
    if (input?.font) return `Updating Font (${input.font})`;
    if (input?.format) return `Switching Template (${input.format})`;
  }

  if (tool.name === 'compute_job_match' && output && output.score !== undefined) {
    return `Scoring ATS Match (${output.score}%)`;
  }

  if (tool.name === 'create_resume_variant') {
    const name = input?.name || output?.name;
    if (name) return `Creating Version (${name})`;
  }

  return null;
};

const formatToolName = (tool) => {
  // 1. If explicit descriptive uiMessage is provided by the agent
  if (tool.input && tool.input.uiMessage) {
    const msg = String(tool.input.uiMessage).trim();
    if (msg && !['x', 'running', 'reading', 'updating'].includes(msg.toLowerCase())) {
      return msg.length > 48 ? `${msg.slice(0, 48).trimEnd()}…` : msg;
    }
  }

  // 2. Dynamic section/entity-aware label
  const dynamic = getDynamicSectionLabel(tool);
  if (dynamic) return dynamic;

  // 3. Static fallback labels
  switch(tool.name) {
    case 'get_resume': return 'Reading Resume & Design';
    case 'update_resume_entry': return 'Updating Resume';
    case 'batch_update_resume': return 'Tailoring Resume';
    case 'update_design_settings': return 'Updating Layout & Typography';
    case 'add_resume_entry': return 'Adding to Resume';
    case 'delete_resume_entry': return 'Removing from Resume';
    case 'compute_job_match': return 'Scoring ATS Job Match';
    case 'verify_ats': return 'Running ATS Audit';
    case 'create_resume_variant': return 'Creating Version Branch';
    case 'scrape_url': return 'Scraping URL';
    case 'search_web': return 'Searching Web';
    case 'search_knowledge_base': return 'Searching Memories';
    case 'search_zaga_memory': return 'Searching Memories';
    case 'search_jobs': return 'Searching Jobs';
    case 'get_job_feed': return 'Reading Your Feed';
    case 'get_job': return 'Opening The Posting';
    case 'explain_job_fit': return 'Checking The Fit';
    case 'list_my_applications': return 'Reading Your Applications';
    case 'whats_missing': return 'Checking What Is Missing';
    case 'save_job': return 'Saving The Job';
    case 'mark_applied': return 'Marking As Applied';
    case 'get_answer_bank': return 'Reading Your Answers';
    case 'save_answer': return 'Saving Your Answer';
    case 'confirm_answer': return 'Confirming Your Answer';
    case 'save_story_draft': return 'Drafting Your Story';
    case 'read_job_posting': return 'Reading The Posting';
    case 'get_resume_material': return 'Reading Your Resume';
    case 'pin_draft_answer': return 'Pinning The Draft';
    case 'approve_draft_answer': return 'Approving The Draft';
    case 'draft_cover_letter': return 'Drafting The Letter';
    case 'queue_job': return 'Queuing The Application';
    case 'cancel_run': return 'Cancelling The Run';
    case 'list_my_runs': return 'Reading Your Runs';
    case 'run_status': return 'Reading The Run';
    case 'navigate': return 'Navigating';
    case 'plan_steps': return 'Planning';
    case 'get_application': return 'Reading The Application';
    case 'arrange_resume_sections': return 'Arranging Your Sections';
    default:
      return tool.name
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
  }
};

// The same empty list for every reply without cards, so a row's props do not
// change between renders for nothing.
const NONE = Object.freeze([]);
const byMessage = (list) => {
  const map = new Map();
  for (const item of list || []) {
    if (!map.has(item.messageId)) map.set(item.messageId, []);
    map.get(item.messageId).push(item);
  }
  return map;
};

/**
 * One message in the conversation. Memoised: a finished reply is drawn again
 * only when it, its cards or its place at the end change. `busy` is true only
 * for the last message while a turn works.
 */
const MessageRow = React.memo(function MessageRow({
  msg, isLast, busy, stepsOpen, thinkingOpen, onToggleSteps, onToggleThinking,
  draftCardState, setDraftCardState, openApplication, changes, cardShown, proposals,
  startedJobs, activeSessionId, regenerate, sendMessage, modelMenu, admin,
}) {
  const card = autoApplyCard(msg.content);
  return (
    <div className={`ag-msg animate-slide-up ${msg.role === 'ai' ? 'ag-ai-msg-row' : ''}`}>
      {card ? (() => {
        // Two cards. The old instruction from the page to the
        // assistant (a user-side card, kept for old sessions), and
        // the server pass's report — drawn on the assistant's side,
        // because it IS the answer: what was done, and which
        // questions are the person's, named. Drawn as the person's
        // own message it read as a question nobody answered
        // (founder, 2026-09-24).
        const lines = autoApplyLines(card);
        if (card.jobCard) {
          // One job, live: its steps, and what is left as buttons
          // that open the job's card on the page.
          const openJob = () => openApplication(card.jobId);
          return (
            <div className={`ag-auto-card is-ai${card.working ? ' is-working' : ''}`} role="status" aria-busy={card.working ? 'true' : undefined}>
              <span className="material-symbols-rounded ag-auto-icon" aria-hidden="true">auto_awesome</span>
              <div className="ag-auto-body">
                <strong>Auto-apply · {card.company}</strong>
                <ol className="ag-auto-steps">
                  {card.steps.map((x, i) => <li key={i} className={`is-${x.state}`}>{x.text}</li>)}
                </ol>
                {card.questions.length > 0 && (
                  <div className="ag-auto-qs">
                    {card.questions.map((q, i) => (
                      <button key={i} type="button" className="ag-auto-q" onClick={() => openJob(q)}>{q}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        }
        return (
          <div className={`ag-auto-card${card.done || card.working ? ' is-ai' : ''}${card.working ? ' is-working' : ''}`} role="status" aria-busy={card.working ? 'true' : undefined}>
            <span className="material-symbols-rounded ag-auto-icon" aria-hidden="true">auto_awesome</span>
            <div className="ag-auto-body">
              <strong>Auto-apply</strong>
              <span className="ag-auto-sum">{autoApplySummary(card)}</span>
              {card.job && <span className="ag-auto-job">{card.job.company} · {card.job.title}</span>}
              {lines.length > 0 && (
                <ul className="ag-auto-list">
                  {lines.map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              )}
            </div>
          </div>
        );
      })() : (
      <div className={`${msg.role === 'user' ? 'ag-user-msg' : 'ag-ai-msg'}${msg.isError ? ' ag-msg-error' : ''}`}>
        {/* While the turn works: the one status line, above the reply
            until its first words arrive (chat-2, 2026-10-30), never
            the list of steps. Once it is done, the steps
            (workSteps.js) fold under "Worked for 38s": a read looks
            like a read, a change gets a tick only when the server
            read it back, a refused step is not shown (founder,
            2026-09-27: the chat should look like Claude's). */}
        {msg.role === 'ai' && (() => {
          if (busy) {
            return msg.live && !msg.content
              ? <StatusLine msg={msg} label={statusLabel(msg.live, msg.live.tool ? formatToolName({ name: msg.live.tool }) : null)} />
              : null;
          }
          const steps = visibleSteps(msg.tools || []);
          const plan = planOf(msg.tools || []);
          if (!steps.length && !plan) return null;
          const open = Boolean(stepsOpen);
          const summary = msg.workedMs != null
            ? `Worked for ${turnClock(msg.workedMs)}`
            : `${steps.length} step${steps.length === 1 ? '' : 's'}`;
          return (
            <div className={`ag-steps${open ? ' is-open' : ''}`}>
              <button type="button" className="ag-steps-toggle" aria-expanded={open} onClick={() => onToggleSteps(msg.id)}>
                {summary}
                <span className="material-symbols-rounded" aria-hidden="true">{open ? 'expand_less' : 'expand_more'}</span>
              </button>
              {open && (plan || steps.length > 0) && (
                <div className="ag-steps-list">
                  {plan && (
                    <ol className="ag-plan" aria-label="Plan">
                      {plan.map((step, i) => <li key={i}>{step}</li>)}
                    </ol>
                  )}
                  {steps.map((tool, tIdx) => {
                    const view = stepView(tool, formatToolName(tool));
                    return (
                      <div
                        key={tIdx}
                        className={`ag-tool-block${view.tone ? ` ag-tool-${view.tone}` : ''}`}
                        // The untruncated description, or what the check read back.
                        title={tool.checked || tool.input?.uiMessage || tool.name}
                      >
                        {view.state === 'running' ? <div className="ag-spinner" /> : (
                          <span className="material-symbols-rounded" style={{ fontSize: '15px' }} aria-hidden="true">{view.icon}</span>
                        )}
                        {view.text}
                        {['running', 'read', 'done'].includes(view.state) && <> <ToolElapsed tool={tool} /></>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* The heartbeat stopped: the server pings every ten
            seconds, so this is a dead connection, not a slow model. */}
        {msg.role === 'ai' && msg.lost && (
          <div className="ag-tool-block ag-tool-failed" role="status">
            <span className="material-symbols-rounded" style={{ fontSize: '14px' }} aria-hidden="true">wifi_off</span>
            Lost the connection.
            <button type="button" className="ag-link-btn" onClick={regenerate}>Try again</button>
          </div>
        )}

        {/* ── Draft review cards ─────────────────────────────────
            The same propose-then-approve idiom as the resume
            agent's DiffCard: a pinned draft renders as a card with
            the words IN FULL and the verbs on it. Approving here
            and approving on the queue row are the same server
            call — two surfaces, one truth. */}
        {msg.role === 'ai' && msg.tools && msg.tools
          .filter(t => t.name === 'pin_draft_answer' && t.status !== 'running' && t.status !== 'failed')
          .map((t, dIdx) => {
            let out = null;
            try { out = typeof t.output === 'string' ? JSON.parse(t.output) : t.output; } catch { out = null; }
            if (!out || out.status !== 'SUCCESS' || !out.question) return null;
            const ck = `${msg.id}:${out.question}`;
            const state = draftCardState[ck];
            return (
              <div key={`draft-${dIdx}`} className="ag-draft-card">
                <div className="ag-draft-card-head">
                  <span className="material-symbols-rounded">edit_note</span>
                  Drafted for this application. Yours to approve
                </div>
                <p className="ag-draft-card-q">{out.question}</p>
                <p className="ag-draft-card-a">“{out.answer}”</p>
                {state ? (
                  <p className={`ag-draft-card-done${state === 'discarded' ? ' is-discarded' : ''}`}>
                    {state === 'approved'
                      ? 'Approved. It will fill on this application.'
                      : 'Discarded.'}
                  </p>
                ) : (
                  <div className="ag-draft-card-actions">
                    <button
                      type="button"
                      className="ag-draft-approve"
                      onClick={async () => {
                        try {
                          await approveDraft(out.jobId, out.question);
                          setDraftCardState(d => ({ ...d, [ck]: 'approved' }));
                          openApplication(out.jobId);
                        } catch (err) { console.error(err); }
                      }}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="ag-draft-reject"
                      onClick={async () => {
                        try {
                          await discardDraft(out.jobId, out.question);
                          setDraftCardState(d => ({ ...d, [ck]: 'discarded' }));
                          openApplication(out.jobId);
                        } catch (err) { console.error(err); }
                      }}
                    >
                      Discard
                    </button>
                    <span className="ag-draft-card-hint">Want changes? Edit it on the queue row, or just tell me.</span>
                  </div>
                )}
              </div>
            );
          })}

        {/* Message content. Parsed for <thought> tags to render Claude-style thinking */}
        {(() => {
          const parsed = parseMessageContent(msg.content);
          const { thinking } = parsed;
          // An older server's next-actions block is never shown as
          // words, even half-written (replyText.js).
          const body = msg.role === 'ai' ? stripNext(parsed.body, { streaming: busy }) : parsed.body;

          return (
            <>
              {thinking && (
                <div className="ag-thinking-container">
                  <button
                    type="button"
                    className="ag-thinking-toggle"
                    onClick={() => onToggleThinking(msg.id)}
                  >
                    <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>
                      {thinkingOpen ? 'expand_less' : 'expand_more'}
                    </span>
                    <span>Thinking process</span>
                  </button>
                  {thinkingOpen && (
                    <div className="ag-thinking-content animate-fade-in">
                      <Md text={thinking} />
                    </div>
                  )}
                </div>
              )}

              {/* What the server said about this turn before the
                  reply ("Using the faster model until 3:10 pm."):
                  muted, one line each, above the words. */}
              {msg.role === 'ai' && msg.notices?.length > 0 && (
                <div className="ag-notices">
                  {msg.notices.map((n, i) => <p key={i} className="ag-notice">{n.message}</p>)}
                </div>
              )}

              <div className="ag-msg-markdown">
                <Md text={body || ''} />
              </div>

              {/* A status after the reply's first words (its check, a
                  late step) sits under them, where the reply is
                  being written. */}
              {msg.role === 'ai' && busy && msg.live && msg.content && (
                <StatusLine msg={msg} label={statusLabel(msg.live, msg.live.tool ? formatToolName({ name: msg.live.tool }) : null)} />
              )}

              {/* One card per turn, not per edit — see DiffCard.js. */}
              {changes.length > 0 || cardShown
                ? <DiffCard key={`diff-${msg.id}`} changes={changes} />
                : null}

              {/* A new arrangement of the resume's sections, decided
                  on its own card — see ArrangeCard.js. */}
              {msg.role === 'ai' && proposals.map(p => <ArrangeCard key={p.proposalId} proposal={p} />)}

              {/* What only the person can answer for an application
                  this reply started — see AskOnce.js. */}
              {msg.role !== 'user' && startedJobs?.length > 0 && (
                <AskOnce key={`ask-${msg.id}`} jobs={startedJobs} />
              )}

            </>
          );
        })()}

        {/* Only on a finished assistant reply. Showing these while
            text is still streaming offers a copy of half an answer
            and a regenerate of something that has not happened. */}
        {/* Where the result lives, as the design draws it: the page's
            icon, what was found, and the rest of it in grey. A reply
            that proposed resume changes already carries its own card
            to the review, so it gets no second one. */}
        {msg.role !== 'user' && (() => {
          const found = lastPanel(msg.tools);
          const ownCard = cardShown || proposals.length > 0;
          if (!found || (found.tab === 'Resume' && ownCard)) return null;
          const [title, ...rest] = String(found.focus?.label || found.tab).split(' · ');
          return (
            <Link className="ag-panel-link" to={panelUrl(found)}>
              <span className="ag-panel-link-icon" aria-hidden="true">
                <span className="material-symbols-rounded">{PANELS.find(p => p.label === found.tab)?.icon || 'open_in_new'}</span>
              </span>
              <span className="ag-panel-link-text">
                <strong>{title}</strong>{rest.length > 0 && <>{' '}<span>{rest.join(' · ')}</span></>}
              </span>
              <span className="material-symbols-rounded ag-panel-link-go" aria-hidden="true">arrow_forward</span>
            </Link>
          );
        })()}
        {msg.role !== 'user' && msg.content && !busy && (
          <MessageActions
            message={msg}
            sessionId={activeSessionId}
            canRegenerate={isLast}
            onRegenerate={regenerate}
          />
        )}
        {/* Which model answered ("Auto · Claude Sonnet 5.5"), then
            the receipt; once the turn is done. */}
        {msg.role === 'ai' && !msg.isError && !busy && (() => {
          const line = turnFooter(msg, modelMenu, { admin });
          return line ? (
            <div className="ag-usage-footer" title="Which model answered this, and what it used">
              {line}
            </div>
          ) : null;
        })()}

        {/* Two or three next steps the server suggested (chat-2),
            under the latest finished reply only: older ones would
            offer to continue a conversation that has moved on. A
            chip sends its prompt as the person's next message;
            "Think harder" asks the last question again on the deep
            route, in place of this answer (regenerate). */}
        {msg.role === 'ai' && isLast && !busy && msg.suggestions?.length > 0 && (
          <div className="ag-followup-chips" role="group" aria-label="Suggested next steps">
            {msg.suggestions.map((s, i) => (
              <button
                key={`${s.label}-${i}`}
                type="button"
                className={`ag-followup-chip${s.kind === 'think_harder' ? ' is-deep' : ''}`}
                onClick={() => (s.kind === 'think_harder' ? regenerate({ thinkHarder: true }) : sendMessage(s.prompt))}
              >
                {s.kind === 'think_harder' && <span className="material-symbols-rounded" aria-hidden="true">psychology</span>}
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
});

// The setup in the chat, loaded when it is first shown (2026-10-31): it and
// the career-profile fields it asks with are about 36 KB that every visitor
// parsed before the first paint, for a screen a person sees once.
const SetupThreadChunk = lazy(() => import('../Setup/SetupThread'));
const SetupThread = (props) => (
  <Suspense fallback={null}><SetupThreadChunk {...props} /></Suspense>
);

// The model picker and the meter, drawn again only when what they show
// changes, not for each keystroke in the box above them.
const ModelPickerMemo = React.memo(ModelPicker);
const UsageMeterMemo = React.memo(UsageMeter);

/**
 * The box the person types in, with its @-mentions, the model picker and the
 * meter. Its words are its own state, so a keystroke draws this and nothing
 * else. `draft` (a ref the chat holds) keeps unsent words across the chat
 * being closed and opened again, as when they lived in the chat itself.
 */
const Composer = React.memo(function Composer({
  draft, placeholder, onImportResume, mentions, setupTurn, isProcessing,
  activeEditContext, setActiveEditContext, sendMessage, stopGeneration,
  modelMenu, activeModel, setActiveModel, plan, meter, thinkHarder, setThinkHarder,
}) {
  const [input, setInputState] = useState(() => draft.current);
  const setInput = (val) => { draft.current = val; setInputState(val); };
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const textareaRef = useRef(null);

  // Words kept from before the chat closed fill the box at its height.
  useEffect(() => {
    const box = textareaRef.current;
    if (box && box.value) { box.style.height = 'auto'; box.style.height = `${Math.min(box.scrollHeight, 180)}px`; }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInput(val);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 180)}px`;
    }
    const cursor = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursor);
    const match = textBeforeCursor.match(/(?:^|\s)@(\w*)$/);
    // @ mentions resume SECTIONS, so the menu exists only where the resume
    // does. On the job agent an @ is just a character — offering Summary /
    // Experience there pointed at a document this agent cannot even touch.
    if (match && mentions) {
      setMentionQuery(match[1].toLowerCase());
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  const handleSelectMention = (section) => {
    const newText = input.replace(/(?:^|\s)@\w*$/, ' ');
    setInput(newText);
    setMentionQuery(null);
    setActiveEditContext({ id: section.id, title: section.title });
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!input.trim()) return;
    // Typed during setup, a turn is setup's: free (the server checks).
    sendMessage(input, { setup: setupTurn });
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const mentioned = mentionQuery !== null
    ? AVAILABLE_SECTIONS.filter(s => s.title.toLowerCase().startsWith(mentionQuery))
    : NONE;

  return (
    <div className="ag-input-area" style={{ position: 'relative' }}>
      {mentionQuery !== null && (
        <div
          className="ag-mention-popover bg-white shadow-lg border rounded p-1 position-absolute w-100"
          style={{ bottom: '100%', left: 0, zIndex: 1060, marginBottom: '8px', maxHeight: '200px', overflowY: 'auto' }}
        >
          {mentioned.map((sec, idx) => (
            <div
              key={sec.id}
              onClick={() => handleSelectMention(sec)}
              className={`p-2 rounded ${idx === mentionIndex ? 'bg-light fw-bold text-primary' : 'text-secondary'}`}
              style={{ cursor: 'pointer', fontSize: '0.85rem' }}
            >
              <span className="me-2 opacity-50">@</span>{sec.title}
            </div>
          ))}
          {mentioned.length === 0 && (
            <div className="p-2 text-muted" style={{ fontSize: '0.8rem' }}>No sections found...</div>
          )}
        </div>
      )}

      {activeEditContext && (
        <div className="ag-context-pill-container mb-2">
          <div className="ag-context-pill">
            <span className="ag-context-pill-icon">@</span>
            {activeEditContext.title}
            <button
              onClick={() => setActiveEditContext(null)}
              className="btn btn-link p-0 ms-1 text-decoration-none"
              style={{ color: 'inherit', lineHeight: 1 }}
            >
              &times;
            </button>
          </div>
        </div>
      )}
      <form className="ag-input-wrapper" onSubmit={handleSubmit}>
        <div className="ag-input-top-row w-100">
          <textarea
            ref={textareaRef}
            rows={1}
            className="ag-input"
            placeholder={placeholder || 'Ask anything'}
            value={input}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (mentionQuery !== null) {
                const filtered = mentioned;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setMentionIndex(p => (p + 1) % Math.max(1, filtered.length));
                  return;
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setMentionIndex(p => (p - 1 + filtered.length) % Math.max(1, filtered.length));
                  return;
                }
                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault();
                  if (filtered[mentionIndex]) handleSelectMention(filtered[mentionIndex]);
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setMentionQuery(null);
                  return;
                }
              }

              // Enter submits; Shift + Enter creates a new line. On a
              // touch screen Return is the new line and Send sends.
              if (e.key === 'Enter' && !e.shiftKey && !returnBreaksLine()) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
        </div>
        <div className="ag-input-bottom-row w-100 d-flex justify-content-between align-items-center mt-2">
          <div className="ag-composer-tools d-flex align-items-center gap-2">
            {/* The + opens the resume importer. It was a button with no
                onClick at all, so it looked like an affordance and did
                nothing when pressed, which is worse than not being there.
                On the dashboard no importer is passed and it stays inert,
                so it is hidden rather than left dead. */}
            {onImportResume ? (
              <button
                type="button"
                className="ag-icon-btn"
                onClick={onImportResume}
                title="Import a resume"
              >
                <span className="material-symbols-rounded" style={{ fontSize: '20px' }}>add</span>
              </button>
            ) : null}
            {/* Auto, a model, and Think harder: the server's menu only
                (ModelPicker.js). The pick holds for this conversation. */}
            <div className="ag-model-wrap">
              <ModelPickerMemo
                menu={modelMenu}
                activeModel={activeModel}
                onSelect={setActiveModel}
                plan={plan}
                meter={meter}
                thinkHarder={thinkHarder}
                onThinkHarder={setThinkHarder}
              />
            </div>
          </div>

          <div className="ag-composer-send d-flex align-items-center gap-2">
            {/* Send doubles as Stop mid-response. Without a way to
                interrupt, a user who realised they'd asked for the wrong
                thing had to sit through the whole turn — and aborting the
                fetch also tells the backend to stop the graph. */}
            {isProcessing ? (
              <button
                type="button"
                className="ag-send-btn"
                onClick={stopGeneration}
                title="Stop"
                aria-label="Stop"
              >
                <span className="ag-stop-square" aria-hidden="true" />
              </button>
            ) : (
              <button type="submit" className="ag-send-btn" disabled={!input.trim()} aria-label="Send" title="Send">
                <span className="material-symbols-rounded" aria-hidden="true">arrow_upward</span>
              </button>
            )}
          </div>
        </div>
      </form>
      {/* How much of the plan is left: "Plenty left" until the window or
          the week passes 70% (UsageMeter.js). */}
      {meter && <UsageMeterMemo meter={meter} />}
    </div>
  );
});

// `embedded`: when true, renders permanently docked in-flow instead of as
// a centered modal overlay. No backdrop, no close button, no Escape-to-
// close — there's nothing to dismiss when the chat is a fixed part of the
// page layout.
// `layout`: only matters when embedded. 'docked' (default) is a fixed-width
// column with a border, used by Design Studio's chat column. 'full' is a
// borderless, edge-to-edge shell with an inner centered reading column,
// used by Dashboard where the chat *is* the entire page.
// `onCollapse`: optional. When provided, shows a "hide chat" control in the
// top-right corner (the same spot the old non-embedded close button used).
// The component itself stays agnostic about what collapsing means — the
// parent (e.g. ProfileContainer.js) owns whether/how it stops rendering
// this column and how to bring it back.
// onOpenVersionHistory / onImportResume: supplied by Design Studio only.
//
// These live in the workspace's overflow menu as well, but a three-dot menu is
// where a feature goes to be forgotten, and this app's story is that the
// assistant does the work. Surfacing them here costs nothing on the dashboard,
// which passes neither and renders neither.
//
// ATS needs no prop. It is a question the assistant can already answer with
// its verify_ats tool, so the button sends the question rather than opening a
// separate report — the check runs, and the answer arrives in the conversation
// like everything else it does.
const AntigravitySidebar = ({
  embedded = false,
  layout = 'docked',
  onCollapse = null,
  onOpenVersionHistory = null,
  onImportResume = null,
  // The composer's hint. The studio's phone screen passes a shorter one;
  // "@ to mention, / for actions" is for people with a keyboard.
  placeholder = null,
}) => {
  const {
    messages,
    sendMessage,
    isProcessing,
    isSidebarOpen,
    toggleSidebar,
    activeModel,
    setActiveModel,
    modelMenu,
    usageSummary,
    meter,
    thinkHarder,
    setThinkHarder,
    activeEditContext,
    setActiveEditContext,
    pendingChanges,
    sectionProposals = NONE,
    activeSessionId,
    regenerate,
    startNewSession,
    stopGeneration,
    choosePanel
  } = useOmniAgent();
  // The applications each reply started (OmniAgentContext).
  const { startedBy } = usePanelResults();

  // Whether there is a resume to act on at all. The suggestion chips below
  // depend on it, and offering the wrong ones is worse than offering none.
  const { profileData, loading: profileLoading } = useProfileData();
  const hasResume = Boolean(profileData?.entries?.length);

  // The transcript minus the hidden accept/reject notes. Kept as its own list
  // because "is this the last message" has to be asked of what is on screen —
  // comparing a filtered index against the unfiltered length puts the action
  // row on the wrong message the moment a note exists.
  const visibleMessages = useMemo(() => messages.filter(m => !m.hidden), [messages]);

  // Turns that have shown a diff card at some point. A ref rather than state
  // because it only decides whether to keep rendering something already on
  // screen — writing it during render is idempotent, and making it state would
  // schedule a second render for no visible change.
  const cardsShownRef = useRef(new Set());

  const location = useLocation();
  const navigate = useNavigate();
  // Stable, so a reply's row is not drawn again because this was re-made.
  const latest = useRef({});
  latest.current = { navigate, choosePanel };
  const openApplication = useCallback(jobId => {
    latest.current.choosePanel?.('Applications');
    latest.current.navigate(`/job-agent?job=${encodeURIComponent(jobId)}`);
  }, []);
  const { currentUser } = useAuth();
  // Unsent words, kept while the chat is closed (Composer).
  const draft = useRef('');
  // Draft cards the person has acted on this session: 'approved' | 'discarded',
  // keyed messageId:question. The pin itself lives server-side; this only
  // remembers which card was pressed so it can say so instead of re-offering.
  const [draftCardState, setDraftCardState] = useState({});

  const [showThinking, setShowThinking] = useState({});
  const toggleThinking = useCallback((id) => setShowThinking(prev => ({ ...prev, [id]: !prev[id] })), []);
  // A finished turn's steps fold under "Worked for 38s", as Claude shows them;
  // a turn in progress shows only its status line.
  const [openSteps, setOpenSteps] = useState({});
  const toggleSteps = useCallback((id) => setOpenSteps(prev => ({ ...prev, [id]: !prev[id] })), []);

  const endRef = useRef(null);
  const chatBodyRef = useRef(null);
  // The markdown renderer is its own chunk: fetched as soon as a chat exists.
  useEffect(() => { preloadMarkdown(); }, []);

  const firstName = currentUser?.displayName?.split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const timeGreeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  // The AI lives throughout the site, but this centered-overlay form is now
  // only a narrow fallback — not the default entry point. Dashboard hosts
  // its own full-page embedded chat at any width (see Dashboard.js), and
  // Design Studio hosts its own docked chat column on desktop (see
  // ProfileContainer.js). Design Studio's docked column is hidden below the
  // lg breakpoint, so on mobile there'd otherwise be no way to reach it —
  // this overlay covers exactly that one gap. Every other route (Tracker,
  // Zaga, Import, Admin, ...) has no embedded chat of its own yet; rather
  // than pop this overlay there, "New Chat" / "Recent Chats" in the nav
  // send the user to Dashboard instead (see NavigationPanel.js).
  const onDesignStudio = location.pathname.includes('/design-studio');
  const onJobAgent = location.pathname.includes('/job-agent');
  const onInterviewCoach = location.pathname.includes('/interview-coach');
  const onJobFeed = location.pathname.includes('/for-you');

  // One home for every page: a greeting and four things to ask. The prompts
  // follow the page the person is on, so the Resume page offers resume work.
  const homePrompts = onDesignStudio
    ? (hasResume ? RESUME_SUGGESTIONS : EMPTY_RESUME_SUGGESTIONS)
    : onInterviewCoach ? INTERVIEW_COACH_SUGGESTIONS
    : onJobFeed ? JOB_FEED_SUGGESTIONS
    : onJobAgent ? JOB_AGENT_SUGGESTIONS
    : HOME_PROMPTS;
  const isEmpty = visibleMessages.length === 0;

  // ── The setup, in the chat (founder, 2026-09-28) ─────────────────────────
  // The empty home chat leads a person through setup (components/Setup):
  // always until there is a resume, the one thing required besides a name,
  // and after that when they ask for it ("Finish setup"). Only the full chat
  // (the shell's home, a phone's chat screen) carries it.
  const setupHere = embedded && layout === 'full';
  const [setup, setSetup] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  // Whether the setup has been asked about at all (answered or failed): the
  // landing page's waiting question (below) must not jump ahead of a setup
  // that is still on its way.
  const [setupKnown, setSetupKnown] = useState(false);
  useEffect(() => {
    if (!setupHere || !currentUser || !isEmpty) return undefined;
    let off = false;
    // Its code, fetched alongside it, so the setup is drawn as soon as it is known.
    import('../Setup/SetupThread').catch(() => {});
    fetchSetup().then(s => { if (!off) setSetup(s); }).catch(() => {}).finally(() => { if (!off) setSetupKnown(true); });
    return () => { off = true; };
  }, [setupHere, currentUser, isEmpty]);
  // Shown once without a resume, it stays open through the questions that
  // follow the upload, until the person closes it.
  useEffect(() => { if (setup && !setup.hasResume) setSetupOpen(true); }, [setup]);
  const showSetup = Boolean(setupHere && isEmpty && setup && (!setup.hasResume || setupOpen));
  const homeEmpty = isEmpty && !showSetup;
  // The question a visitor typed on the landing page before signing up
  // (utils/pendingPrompt.js): asked here, once, as their first question, as
  // soon as the home chat is free. A new account does the setup first.
  useFirstQuestion(Boolean(setupHere && currentUser && setupKnown && homeEmpty), sendMessage);
  const setupLeft = setup ? setup.progress.total - setup.progress.done : 0;

  // The setup asks the About you page's list (founder, 2026-09-28), so the
  // two keep each other current. "Continue in chat" there opens the setup
  // here, in a fresh conversation when this one has started (on a phone it
  // arrives as ?setup=1 on the home); an answer changed there is re-read here.
  const isEmptyNow = useRef(isEmpty);
  isEmptyNow.current = isEmpty;
  useEffect(() => {
    if (!setupHere) return undefined;
    const open = () => {
      if (!isEmptyNow.current) startNewSession();
      setSetupOpen(true);
      fetchSetup().then(setSetup).catch(() => {});
    };
    const changed = (e) => { if (e?.detail?.from === 'about-you') fetchSetup().then(setSetup).catch(() => {}); };
    window.addEventListener(OPEN_SETUP, open);
    window.addEventListener(FACTS_CHANGED, changed);
    return () => {
      window.removeEventListener(OPEN_SETUP, open);
      window.removeEventListener(FACTS_CHANGED, changed);
    };
    // startNewSession is the context's, stable in use.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupHere]);
  useEffect(() => {
    if (!setupHere || location.pathname !== HOME_PATH || !new URLSearchParams(location.search).has('setup')) return;
    navigate(HOME_PATH, { replace: true });
    openSetup();
  }, [setupHere, location.search, location.pathname, navigate]);
  // The Get started bar across the top already says what is next, with its
  // own count; while it shows, the home's "Finish setup" would be a second
  // prompt with a second number.
  const gettingStarted = useGettingStarted();
  const gettingStartedShowing = gettingStarted.loaded && !gettingStarted.complete && !gettingStarted.hidden;
  // With room for both, the page opens beside the setup and fills in as it
  // goes: once each time the setup opens, never again after the person
  // closes it (a closed panel is their choice). Below 1200px the panel would
  // cover the chat, so it stays shut.
  const { inShell } = useShell();
  const panelOpened = useRef(false);
  useEffect(() => {
    if (!showSetup) { panelOpened.current = false; return; }
    if (panelOpened.current || !inShell || window.innerWidth < 1200) return;
    panelOpened.current = true;
    if (location.pathname === HOME_PATH) navigate(ABOUT_YOU_PATH);
  }, [showSetup, inShell, location.pathname, navigate]);

  // Tablets only. At 768 and below the studio embeds this chat as its own
  // Chat screen (ProfileContainer.js), so an overlay there would be a
  // second copy over the first.
  const isOverlayEligible = onDesignStudio && window.innerWidth > 768 && window.innerWidth < 992;
  const isVisible = embedded || (isOverlayEligible && isSidebarOpen);

  // pendingChanges is in the dependency list, not just messages.
  //
  // The diff card is driven by pendingChanges — separate state, in a separate
  // context, arriving after the message text it attaches to. Scrolling only on
  // `messages` meant the card mounted below the fold with nothing to bring it
  // into view, so the reply said "accept the card" and the Accept/Reject
  // buttons were simply off screen. It got worse once the card started showing
  // full bullet content, because taller cards fall further past the edge.
  //
  // Once a frame at most (2026-10-31). Reading scrollHeight makes the browser
  // lay the page out there and then; done for every streamed word, several
  // words landing in one frame meant several layouts of the whole
  // conversation for one picture, the largest cost left in a reply.
  const scrollFrame = useRef(0);
  useEffect(() => {
    if (!chatBodyRef.current || !isVisible || scrollFrame.current) return;
    scrollFrame.current = requestAnimationFrame(() => {
      scrollFrame.current = 0;
      const body = chatBodyRef.current;
      if (body) body.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
    });
  }, [messages, pendingChanges, isVisible]);
  useEffect(() => () => cancelAnimationFrame(scrollFrame.current), []);

  // Centered overlay: Escape closes it, like any modal. Not applicable
  // when embedded — there's nothing to dismiss.
  useEffect(() => {
    if (embedded || !isSidebarOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') toggleSidebar();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [embedded, isSidebarOpen, toggleSidebar]);

  // Which cards belong to which reply, worked out once per change rather
  // than filtered again for every reply on every render.
  const changesByMessage = useMemo(() => byMessage(pendingChanges), [pendingChanges]);
  const proposalsByMessage = useMemo(() => byMessage(sectionProposals), [sectionProposals]);
  const admin = isAdminUser(currentUser);

  // Out of sight (the tablet overlay, closed; the app shell's copy on a page
  // that has its own chat), nothing is drawn — not even built. It used to
  // build the whole conversation and throw it away, for every streamed word.
  if (!isVisible) return null;

  const cardBody = (
    <>
        <ConversationHeader />
        {/* Named so the studio's phone layout can hide these: there they
            would sit on the greeting, and the strip's overflow menu already
            holds the same actions (claudeTheme.css, .ds-chat-screen). */}
        {/* Minimal Controls */}
        <div className="ag-corner-actions position-absolute d-flex align-items-center gap-1" style={{ top: '16px', right: '16px', zIndex: 10 }}>
          {/* A fresh conversation, right here. The conversation follows the
              person from page to page now (OmniAgentContext), so the one New
              Chat in the navigation, which also jumps to the dashboard, is
              not enough on a page with its own chat. Shown once there is a
              conversation to leave. */}
          {messages.length > 0 && (
            <button
              className="ag-close-btn"
              onClick={startNewSession}
              disabled={isProcessing}
              title="New chat"
              aria-label="New chat"
            >
              <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>edit_square</span>
            </button>
          )}
          {/* Ask the assistant to run the ATS check, rather than opening a
              report beside it. It has a verify_ats tool and the answer belongs
              in the conversation with everything else it tells the user.
              Phrased as a question on purpose: intent.js strips the mutating
              tools from anything read-only, and verify_ats is not one of them,
              so the check still runs and nothing can be edited by accident. */}
          {onDesignStudio && (
            <button
              className="ag-close-btn"
              onClick={() => sendMessage('Is my resume ATS-friendly?')}
              disabled={isProcessing}
              title="Check my resume against ATS"
            >
              <span className="material-symbols-rounded" style={{ fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>fact_check</span>
            </button>
          )}
          {onOpenVersionHistory && (
            <button
              className="ag-close-btn"
              onClick={onOpenVersionHistory}
              title="Version history"
            >
              <span className="material-symbols-rounded" style={{ fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>history</span>
            </button>
          )}
          {onCollapse && (
            <button
              className="ag-close-btn"
              onClick={onCollapse}
              title="Close chat"
            >
              <span className="material-symbols-rounded" style={{ fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>close</span>
            </button>
          )}
          {!embedded && (
            <button
              className="ag-close-btn"
              onClick={toggleSidebar}
              title="Close"
            >
              <span className="material-symbols-rounded" style={{ fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>close</span>
            </button>
          )}
        </div>

        <div className="ag-chat-body" ref={chatBodyRef}>
          {showSetup ? (
            <SetupThread
              setup={setup}
              name={currentUser?.displayName || ''}
              returning={setupOpen && setup.hasResume}
              api={{ save: saveSetup, confirm: confirmSetup, refresh: fetchSetup }}
              onChange={setSetup}
              onBuild={() => sendMessage('Help me build my resume from scratch. Ask me one thing at a time.', { setup: true })}
              onClose={() => setSetupOpen(false)}
            />
          ) : isEmpty ? (
            <div className="ag-home">
              <h1 className="ag-home-greeting">
                <img className="ag-home-mark" src={`${process.env.PUBLIC_URL}/logo.png`} alt="" aria-hidden="true" />
                <span>{timeGreeting}, {firstName}</span>
              </h1>
            </div>
          ) : (
            visibleMessages.map((msg, idx) => {
              const isLast = idx === visibleMessages.length - 1;
              const changes = changesByMessage.get(msg.id) || NONE;
              if (changes.length > 0) cardsShownRef.current.add(msg.id);
              return (
                <MessageRow
                  key={msg.id || idx}
                  msg={msg}
                  isLast={isLast}
                  busy={isLast && isProcessing}
                  stepsOpen={Boolean(openSteps[msg.id])}
                  thinkingOpen={Boolean(showThinking[msg.id])}
                  onToggleSteps={toggleSteps}
                  onToggleThinking={toggleThinking}
                  draftCardState={draftCardState}
                  setDraftCardState={setDraftCardState}
                  openApplication={openApplication}
                  changes={changes}
                  cardShown={cardsShownRef.current.has(msg.id)}
                  proposals={proposalsByMessage.get(msg.id) || NONE}
                  startedJobs={startedBy?.get?.(msg.id)}
                  activeSessionId={activeSessionId}
                  regenerate={regenerate}
                  sendMessage={sendMessage}
                  modelMenu={modelMenu}
                  admin={admin}
                />
              );
            })
          )}

          {/* Typing indicator. The condition used to be "last message is from
              the user", but sendMessage appends an empty AI bubble immediately,
              so the last message is always the assistant and the indicator
              never rendered — the UI looked frozen during the first (often
              slowest) leg of a response. Show it whenever we're waiting on an
              assistant turn that hasn't produced text or a tool chip yet. */}
          {isProcessing && messages.length > 0 && (() => {
            const last = visibleMessages[visibleMessages.length - 1];
            return last.role === 'user' || (!last.content && !(last.tools || []).length && !last.live && !last.lost);
          })() && (
            <div className="ag-typing-indicator">
              <div className="ag-dot"></div>
              <div className="ag-dot"></div>
              <div className="ag-dot"></div>
            </div>
          )}

          <div ref={endRef} />
        </div>

        <Composer
          draft={draft}
          placeholder={placeholder}
          onImportResume={onImportResume}
          mentions={onDesignStudio}
          setupTurn={showSetup}
          isProcessing={isProcessing}
          activeEditContext={activeEditContext}
          setActiveEditContext={setActiveEditContext}
          sendMessage={sendMessage}
          stopGeneration={stopGeneration}
          modelMenu={modelMenu}
          activeModel={activeModel}
          setActiveModel={setActiveModel}
          plan={usageSummary?.plan || null}
          meter={meter}
          thinkHarder={thinkHarder}
          setThinkHarder={setThinkHarder}
        />

        {/* Nothing until the resume has loaded: guessing wrong offers a new
            user an action that cannot work, and a beat with no prompts costs
            nothing. */}
        {homeEmpty && (onJobAgent || !profileLoading) && (
          <div className="ag-home-prompts">
            {setupHere && setup && !setup.complete && setupLeft > 0 && !gettingStartedShowing && (
              <button type="button" className="ag-home-prompt ag-home-setup" onClick={() => setSetupOpen(true)}>
                <span className="material-symbols-rounded" aria-hidden="true">checklist</span>
                <span>Finish setup · {setupLeft} left</span>
              </button>
            )}
            {homePrompts.map(card => (
              <button key={card.id} type="button" className="ag-home-prompt" onClick={() => sendMessage(card.prompt)}>
                <span className="material-symbols-rounded" aria-hidden="true">{card.icon}</span>
                <span>{card.title}</span>
              </button>
            ))}
          </div>
        )}
    </>
  );

  return embedded ? (
    <div className={`ag-sidebar-container ag-embedded ag-embedded-${layout}${homeEmpty ? ' ag-is-empty' : ''}`}>
      {cardBody}
    </div>
  ) : (
    <div className="ag-overlay-backdrop" onClick={toggleSidebar}>
      <div className={`ag-sidebar-container${homeEmpty ? ' ag-is-empty' : ''}`} onClick={(e) => e.stopPropagation()}>
        {cardBody}
      </div>
    </div>
  );
};

export default AntigravitySidebar;
